import { Camera, FlaskRound, Radar, RefreshCw, Radio, Settings2 } from "lucide-react";
import { Link } from "react-router-dom";
import DeviceSelector from "components/DeviceSelector";
import { fieldTime } from "utils/fieldFormat";
import "./field.css";

// The field operations pages (Field cameras, Node control, Radar analysis,
// Radar lab): the header with the node selector, the section tabs and the
// refresh. `nodes.locked` keeps the selector on its node (Radar lab, while a
// lab run is going or when the server pins the node).
export default function FieldLayout({
  nodes,
  title,
  subtitle,
  section,
  refresh,
  children,
}) {
  return (
    <div className="field-page">
      <header className="field-header">
        <div>
          <p className="field-eyebrow">
            FIELD OPERATIONS{" "}
            <span>
              {" "}
              /{" "}
              {section === "node"
                ? "NODE"
                : section === "camera"
                ? "SPATIAL VIEW"
                : section === "analysis"
                  ? "RADAR ANALYSIS"
                  : section === "lab"
                    ? "RADAR LAB"
                    : "NODE CONTROL"}
            </span>
          </p>
          <h1>{title}</h1>
          {subtitle && <p className="field-subtitle">{subtitle}</p>}
        </div>
        <div className="field-selector">
          <label htmlFor="field-device">Field node</label>
          <DeviceSelector
            id="field-device"
            devices={nodes.devices.map((d) => ({ ...d, mac_address: d.mac }))}
            value={nodes.mac}
            onChange={nodes.select}
            disabled={!!nodes.locked}
            placeholder={
              nodes.loading ? "Loading nodes…" : "Select a field node"
            }
          />
          <div className="field-muted">
            {nodes.node?.last_seen
              ? `Last report · ${fieldTime(nodes.node.last_seen)} SGT`
              : "Waiting for a device report"}
          </div>
        </div>
      </header>
      <div className="field-toolbar">
        <div className="field-tabs">
          <Link
            className={section === "camera" ? "active" : ""}
            to="/field-cameras"
          >
            <Camera size={16} /> Field cameras
          </Link>
          <Link
            className={section === "control" ? "active" : ""}
            to="/node-control"
          >
            <Settings2 size={16} /> Node control
          </Link>
          <Link
            className={section === "analysis" ? "active" : ""}
            to="/radar-analysis"
          >
            <Radar size={16} /> Radar analysis
          </Link>
          <Link className={section === "lab" ? "active" : ""} to="/radar-lab">
            <FlaskRound size={16} /> Radar lab
          </Link>
        </div>
        <button
          className="field-button"
          onClick={() => {
            nodes.refresh?.();
            refresh?.();
          }}
          disabled={!nodes.mac}
        >
          <RefreshCw size={15} /> Refresh
        </button>
      </div>
      {nodes.error && (
        <div className="field-alert" role="alert">
          {nodes.error}
        </div>
      )}
      {!nodes.mac ? (
        <div className="field-empty">
          <Radio size={32} />
          <h2>
            {nodes.loading
              ? "Connecting to your field nodes"
              : "No field nodes available"}
          </h2>
          <p>
            {nodes.loading
              ? "Loading the devices your account can access."
              : "A registered node will appear when its first authenticated report arrives."}
          </p>
        </div>
      ) : (
        children
      )}
    </div>
  );
}
export function FieldErrors({ errors }) {
  return (
    <>
      {errors.filter(Boolean).map((e, i) => (
        <div key={i} role="alert" className="field-alert">
          {e}
        </div>
      ))}
    </>
  );
}
export function FieldMetric({ label, value, detail, icon: Icon }) {
  return (
    <div className="field-metric">
      <div className="field-metric-label">
        {Icon && <Icon size={16} />}
        {label}
      </div>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
    </div>
  );
}
